import React, { useEffect, useState } from 'react'
import {
  Card,
  Form,
  Row,
  Col,
  Button,
  Skeleton,
  Select,
  Input,
  message,
} from 'antd'
import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { MaskedInput } from 'antd-mask-input'
import { getFormatedPhone, MaskPattern } from 'utils'
import Api from 'api'
import { LoadingOutlined } from '@ant-design/icons'
import { organizationRoleValidation } from 'validations'
import { useOrganizationRoleSelector } from 'store/organizationRolesSlice/OrganizationRolesReducer'
import { useAuthSelector } from 'store/authSlice/authReducer'
import { addRole } from 'store/organizationRolesSlice/OrganizationRolesActions'

const initialValues = {
  countryCode: '+1',
  phoneNumber: '',
  name: '',
  email: '',
}

const AddUserForRole = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { organization } = useAuthSelector()
  const { loading } = useOrganizationRoleSelector()
  const { adding } = loading
  const [fetchingUser, setFetchingUser] = useState(false)
  const [userFound, setUserFound] = useState(false)
  const [user, setUser] = useState(null)
  const [form] = Form.useForm()
  Form.useWatch('countryCode', form)
  Form.useWatch('phoneNumber', form)

  const fetchUser = () => {
    setFetchingUser(true)
    const countryCode = form.getFieldValue('countryCode')
    const phoneNumber = form.getFieldValue('phoneNumber')
    const phone = getFormatedPhone(countryCode, phoneNumber)
    Api.get(
      `/users/check-phone-number-with-info?phone=${encodeURIComponent(phone)}`,
    )
      .then(() => {
        form.setFieldValue('name', '')
        form.setFieldValue('email', '')
        setUserFound(false)
        setUser(null)
        setFetchingUser(false)
        message.info('User not found. Please enter details')
      })
      .catch((err) => {
        const success = err?.response?.data?.message
        const data = err?.response?.data?.values
        if (success === 'User Found') {
          form.setFieldValue('name', data.name)
          form.setFieldValue('email', data.email)
          setUserFound(true)
          setUser(data)
          setFetchingUser(false)
          message.success('User found')
        } else {
          message.error('Something Went Wrong')
        }
      })
  }

  useEffect(() => {
    if (form.getFieldValue('phoneNumber')?.length === 10) {
      fetchUser()
    } else if (fetchingUser) {
      form.setFieldValue('name', '')
      form.setFieldValue('email', '')
      setUserFound(false)
      setUser(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.getFieldValue('countryCode')])

  const handlePhoneNumberChange = (e) => {
    const value = e.unmaskedValue
    form.setFieldsValue({ phoneNumber: value })
    if (value.length === 10) {
      fetchUser()
    } else {
      setUserFound(false)
      setUser(null)
    }
  }

  const navigation = () => {
    form.resetFields()
    history.push('/settings/organization/manage-roles')
  }

  const onFinish = async (values) => {
    const { countryCode, phoneNumber, name, email } = values
    const phone = getFormatedPhone(countryCode, phoneNumber)
    const payload = {
      PhoneNumber: phone,
      Name: userFound ? user?.name : name,
      Email: userFound ? user?.email : email,
      OrganizationId: organization?.id,
    }
    dispatch(addRole(payload, navigation))
  }

  const buttonText = 'Add User'

  const isDisabled = fetchingUser || userFound
  return (
    <Card
      className="header-solid"
      bordered={false}
      title={[
        <>
          <h6 className="mb-0 t-text-2xl">Add User</h6>
          <p className="font-regular">Enter Information</p>
        </>,
      ]}
    >
      <Skeleton loading={false} active paragraph={{ rows: 18 }}>
        <Form
          form={form}
          layout="vertical"
          key={0}
          onFinish={onFinish}
          name="User Form"
          initialValues={initialValues}
          scrollToFirstError
        >
          <Row gutter={[24, 0]}>
            <Col xl={12} md={12} xs={24} sm={12}>
              <Form.Item
                name="countryCode"
                label="Country Code"
                rules={[organizationRoleValidation]}
                className="t-mb-[12px] md:t-mb-[24px]"
              >
                <Select
                  placeholder="Select Country Code"
                  style={{ width: '100%' }}
                >
                  <Select.Option value="+1">
                    United States/Canada (+1)
                  </Select.Option>
                  <Select.Option value="+54">Argentina (+54)</Select.Option>
                  <Select.Option value="+92">Pakistan (+92)</Select.Option>
                  <Select.Option value="+55">Brazil (+55)</Select.Option>
                  <Select.Option value="+58">
                    Venezuela, Bolivarian Republic of Venezuela (+58)
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xl={12} md={12} xs={24} sm={12}>
              <Form.Item
                name="phoneNumber"
                label={
                  <div className="t-flex t-justify-center t-items-center t-gap-2">
                    <div>Mobile Number * </div>
                    {fetchingUser && (
                      <div>
                        <LoadingOutlined />
                      </div>
                    )}
                  </div>
                }
                rules={[organizationRoleValidation]}
                className="t-mb-[12px] md:t-mb-[24px]"
              >
                <MaskedInput
                  defaultValue={form.getFieldValue('phoneNumber')}
                  inputMode="numeric"
                  mask={MaskPattern}
                  onChange={handlePhoneNumberChange}
                  disabled={fetchingUser}
                />
              </Form.Item>
            </Col>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.phoneNumber !== currentValues.phoneNumber
              }
            >
              {({ getFieldValue }) =>
                getFieldValue('phoneNumber')?.length === 10 &&
                !fetchingUser &&
                !userFound ? (
                  <>
                    <Col xl={12} md={12} xs={24} sm={24}>
                      <Form.Item
                        name="name"
                        label="Name"
                        colon={false}
                        rules={[organizationRoleValidation]}
                      >
                        <Input placeholder="Enter Name" disabled={isDisabled} />
                      </Form.Item>
                    </Col>
                    <Col xl={12} md={12} xs={24} sm={24}>
                      <Form.Item
                        name="email"
                        label="Email"
                        colon={false}
                        rules={[organizationRoleValidation]}
                      >
                        <Input
                          placeholder="Enter Email"
                          disabled={isDisabled}
                        />
                      </Form.Item>
                    </Col>
                  </>
                ) : null
              }
            </Form.Item>
            <Col span={24} className="text-right t-space-x-2">
              <Button
                type="primary"
                className="px-25"
                onClick={() => history.goBack()}
              >
                Cancel
              </Button>
              <Button
                disabled={fetchingUser}
                loading={adding}
                type="primary"
                className="px-25"
                htmlType="submit"
              >
                {buttonText}
              </Button>
            </Col>
          </Row>
        </Form>
      </Skeleton>
    </Card>
  )
}

export { AddUserForRole }
